import { FileText, LayoutTemplate, Stethoscope, SlidersHorizontal, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface LeftRailProps {
  showTemplates: boolean;
  onToggleTemplates: () => void;
  // Issues toggle renders only when a handler is provided (feature-flagged
  // off in MainLayout since 2026-07-21).
  showIssues?: boolean;
  onToggleIssues?: () => void;
  issueCount?: number;
}

/**
 * RAAS-style far-left icon navigation rail. The app is single-surface
 * (the reporter), so the rail is intentionally minimal: brand, the active
 * Reporter view, and a toggle for the docked template panel.
 */
export default function LeftRail({ showTemplates, onToggleTemplates, showIssues, onToggleIssues, issueCount }: LeftRailProps) {
  const navigate = useNavigate();
  return (
    <nav className="w-14 bg-surface-950 dark:bg-surface-950 border-r border-gray-200 dark:border-surface-800 flex flex-col items-center pt-2 shrink-0">
      {/* Brand */}
      <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-primary-500 to-primary-700 grid place-items-center text-white mb-4 shadow">
        <Stethoscope size={18} />
      </div>

      {/* Reporter (active) */}
      <RailButton active label="Report" icon={<FileText size={18} />} />

      {/* Templates toggle */}
      <RailButton
        active={showTemplates}
        label="Templates"
        icon={<LayoutTemplate size={18} />}
        onClick={onToggleTemplates}
      />

      {/* Issues toggle — badge shows how many need attention */}
      {onToggleIssues && (
        <RailButton
          active={showIssues}
          label="Issues"
          icon={<AlertTriangle size={18} />}
          onClick={onToggleIssues}
          badge={issueCount && issueCount > 0 ? issueCount : undefined}
        />
      )}

      {/* Onboarding / profile setup */}
      <RailButton
        label="Setup"
        icon={<SlidersHorizontal size={18} />}
        onClick={() => navigate('/onboarding')}
      />
    </nav>
  );
}

function RailButton({
  active = false,
  label,
  icon,
  onClick,
  badge,
}: {
  active?: boolean;
  label: string;
  icon: React.ReactNode;
  onClick?: () => void;
  badge?: number;
}) {
  return (
    <button
      onClick={onClick}
      title={label}
      className={`relative w-11 h-12 mb-1 rounded-lg flex flex-col items-center justify-center gap-0.5 text-[10px] transition-colors ${
        active
          ? 'bg-primary-600/15 text-primary-400'
          : 'text-gray-400 hover:text-gray-200 hover:bg-surface-800'
      }`}
    >
      {icon}
      <span className="leading-none">{label}</span>
      {badge !== undefined && (
        <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold leading-4 text-center">
          {badge > 9 ? '9+' : badge}
        </span>
      )}
    </button>
  );
}
